// The workbook grid: Excel-like. Click or drag to select a range, Shift+click or Shift+arrows to extend, arrows to
// move, Ctrl/Cmd+C to copy as tab-separated values (pastes straight into Excel), Ctrl/Cmd+A for everything.
// The status bar shows Sum, Average and Count of the selection. Columns resize by their header edge; the header
// opens the column menu. Rows are windowed so thousands of rows scroll smoothly; the header, the pinned key
// columns and the totals row stay in view.
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { raw } from "./format";

export type GCol = { key: string; label: string; sub?: string; num: boolean; width: number; pinned?: boolean; sort?: "asc" | "desc" | null; menu?: boolean; warn?: string };
export type GCell = { v: unknown; text: string; bad?: boolean };
export type GRow = { kind: "data" | "sub" | "total"; level?: number; cells: GCell[]; toggle?: { collapsed: boolean; onToggle: () => void } };
export type Stats = { cells: number; count: number; nums: number; sum: number; min: number; max: number };

const ROW_H = 28, NUM_W = 44, OVERSCAN = 12;

export function Grid({ cols, rows, footer, bands, onHeader, onResize, onStats, empty, label }: {
  cols: GCol[]; rows: GRow[]; footer?: GRow | null; bands?: { label: string; span: number }[] | null;
  onHeader?: (i: number, at: DOMRect) => void; onResize?: (key: string, width: number) => void; onStats?: (s: Stats | null) => void; empty?: ReactNode; label: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(0);
  const [height, setHeight] = useState(600);
  const [sel, setSel] = useState<{ r0: number; c0: number; r1: number; c1: number } | null>(null);
  const drag = useRef(false);
  const [widths, setWidths] = useState<Record<string, number>>({});
  const w = (c: GCol) => widths[c.key] ?? c.width;

  useLayoutEffect(() => {
    const el = box.current; if (!el) return;
    const ro = new ResizeObserver(() => setHeight(el.clientHeight));
    ro.observe(el); setHeight(el.clientHeight);
    return () => ro.disconnect();
  }, []);
  useEffect(() => { setSel(null); }, [rows.length, cols.length]);
  useEffect(() => { const up = () => { drag.current = false; }; window.addEventListener("mouseup", up); return () => window.removeEventListener("mouseup", up); }, []);

  // Pinned columns stick at the left, after the row numbers.
  const lefts = useMemo(() => { let x = NUM_W; return cols.map((c) => { const l = c.pinned ? x : null; if (c.pinned) x += w(c); return l; }); }, [cols, widths]); // eslint-disable-line react-hooks/exhaustive-deps

  const norm = sel ? { r0: Math.min(sel.r0, sel.r1), r1: Math.max(sel.r0, sel.r1), c0: Math.min(sel.c0, sel.c1), c1: Math.max(sel.c0, sel.c1) } : null;
  const inSel = (r: number, c: number) => !!norm && r >= norm.r0 && r <= norm.r1 && c >= norm.c0 && c <= norm.c1;

  useEffect(() => {
    if (!onStats) return;
    if (!norm) return onStats(null);
    let count = 0, nums = 0, sum = 0, min = Infinity, max = -Infinity;
    for (let r = norm.r0; r <= norm.r1; r++) for (let c = norm.c0; c <= norm.c1; c++) {
      const v = rows[r]?.cells[c]?.v;
      if (v === null || v === undefined || v === "") continue;
      count++;
      if (typeof v === "number" && Number.isFinite(v)) { nums++; sum += v; min = Math.min(min, v); max = Math.max(max, v); }
    }
    onStats({ cells: (norm.r1 - norm.r0 + 1) * (norm.c1 - norm.c0 + 1), count, nums, sum, min, max });
  }, [sel, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollTo = (r: number) => {
    const el = box.current; if (!el) return;
    const y = r * ROW_H, head = (bands ? 2 : 1) * 32;
    if (y < el.scrollTop) el.scrollTop = y;
    else if (y + ROW_H > el.scrollTop + el.clientHeight - head - (footer ? ROW_H : 0)) el.scrollTop = y + ROW_H - el.clientHeight + head + (footer ? ROW_H : 0);
  };
  const copy = () => {
    if (!norm) return;
    const lines: string[] = [];
    for (let r = norm.r0; r <= norm.r1; r++) { const out: string[] = []; for (let c = norm.c0; c <= norm.c1; c++) out.push(raw(rows[r]?.cells[c]?.v).replace(/[\t\n]/g, " ")); lines.push(out.join("\t")); }
    // Whole columns copy with their headers, the way Excel users expect.
    if (norm.r0 === 0 && norm.r1 === rows.length - 1) lines.unshift(cols.slice(norm.c0, norm.c1 + 1).map((c) => c.label).join("\t"));
    navigator.clipboard?.writeText(lines.join("\n")).catch(() => {});
  };
  const key = (e: React.KeyboardEvent) => {
    const mod = e.ctrlKey || e.metaKey;
    if (mod && e.key.toLowerCase() === "c") { e.preventDefault(); copy(); return; }
    if (mod && e.key.toLowerCase() === "a") { e.preventDefault(); if (rows.length && cols.length) setSel({ r0: 0, c0: 0, r1: rows.length - 1, c1: cols.length - 1 }); return; }
    if (!sel) return;
    const mv: Record<string, [number, number]> = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1], Tab: [0, e.shiftKey ? -1 : 1], Enter: [e.shiftKey ? -1 : 1, 0], PageDown: [Math.floor(height / ROW_H), 0], PageUp: [-Math.floor(height / ROW_H), 0], Home: [0, -999], End: [0, 999] };
    const d = mv[e.key]; if (!d) return;
    e.preventDefault();
    const jump = mod && e.key.startsWith("Arrow");
    const r = Math.max(0, Math.min(rows.length - 1, jump && d[0] ? (d[0] > 0 ? rows.length - 1 : 0) : sel.r1 + d[0]));
    const c = Math.max(0, Math.min(cols.length - 1, jump && d[1] ? (d[1] > 0 ? cols.length - 1 : 0) : sel.c1 + d[1]));
    setSel(e.shiftKey && e.key !== "Tab" && e.key !== "Enter" ? { ...sel, r1: r, c1: c } : { r0: r, c0: c, r1: r, c1: c });
    scrollTo(r);
  };
  const startResize = (e: React.MouseEvent, c: GCol) => {
    e.preventDefault(); e.stopPropagation();
    const x0 = e.clientX, w0 = w(c);
    let last = w0;
    const move = (ev: MouseEvent) => { last = Math.max(48, Math.min(800, w0 + ev.clientX - x0)); setWidths((m) => ({ ...m, [c.key]: last })); };
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); onResize?.(c.key, last); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  };

  const first = Math.max(0, Math.floor(top / ROW_H) - OVERSCAN);
  const last = Math.min(rows.length, Math.ceil((top + height) / ROW_H) + OVERSCAN);
  const tableW = NUM_W + cols.reduce((t, c) => t + w(c), 0);
  const cellStyle = (i: number, c: GCol) => ({ width: w(c), minWidth: w(c), maxWidth: w(c), ...(lefts[i] !== null ? { position: "sticky" as const, left: lefts[i]!, zIndex: 1 } : {}) });

  const rowEl = (row: GRow, r: number, isFoot = false) => (
    <tr key={isFoot ? "foot" : r} className={`g-${row.kind}${row.level !== undefined ? ` g-l${Math.min(row.level, 5)}` : ""}`} style={{ height: ROW_H }}>
      <th className="g-num" scope="row">{isFoot ? "" : r + 1}</th>
      {row.cells.map((cell, i) => {
        const c = cols[i];
        return (
          <td key={i} style={cellStyle(i, c)} className={`${c.num ? "r" : ""}${!isFoot && inSel(r, i) ? " sel" : ""}${!isFoot && sel && sel.r1 === r && sel.c1 === i ? " cur" : ""}${cell.bad ? " bad" : ""}${c.pinned ? " pin" : ""}`}
            title={cell.text.length > 24 ? cell.text : undefined}
            onMouseDown={isFoot ? undefined : (e) => { if (e.button !== 0) return; drag.current = true; box.current?.focus({ preventScroll: true }); setSel(e.shiftKey && sel ? { ...sel, r1: r, c1: i } : { r0: r, c0: i, r1: r, c1: i }); }}
            onMouseEnter={isFoot ? undefined : () => { if (drag.current && sel) setSel({ ...sel, r1: r, c1: i }); }}>
            {i === 0 && row.toggle ? <button className="g-tog" aria-label={row.toggle.collapsed ? "Expand" : "Collapse"} aria-expanded={!row.toggle.collapsed} onMouseDown={(e) => e.stopPropagation()} onClick={row.toggle.onToggle}>{row.toggle.collapsed ? "+" : "−"}</button> : null}
            {cell.text}
          </td>
        );
      })}
      <td className="g-fill" />
    </tr>
  );

  return (
    <div className="grid-box" ref={box} tabIndex={0} role="grid" aria-label={label} aria-rowcount={rows.length} aria-colcount={cols.length} onKeyDown={key} onScroll={(e) => setTop((e.target as HTMLDivElement).scrollTop)}>
      <table className="g" style={{ width: tableW }}>
        <thead>
          {bands && (
            <tr className="g-band">
              <th className="g-num" />
              {bands.map((b, i) => <th key={i} colSpan={b.span} className={b.label ? "" : "blank"}>{b.label}</th>)}
              <th className="g-fill" />
            </tr>
          )}
          <tr>
            <th className="g-num g-corner" onClick={() => rows.length && setSel({ r0: 0, c0: 0, r1: rows.length - 1, c1: cols.length - 1 })} title="Select all" />
            {cols.map((c, i) => (
              <th key={c.key} scope="col" style={cellStyle(i, c)} className={`${c.num ? "r" : ""}${c.pinned ? " pin" : ""}`} aria-sort={c.sort === "asc" ? "ascending" : c.sort === "desc" ? "descending" : undefined}>
                <button className="g-h" title={c.warn ?? c.sub ?? c.label} onClick={(e) => (onHeader ? onHeader(i, (e.currentTarget as HTMLElement).getBoundingClientRect()) : rows.length && setSel({ r0: 0, c0: i, r1: rows.length - 1, c1: i }))}>
                  <span className="g-hl">{c.label}</span>{c.sort && <span className="g-sort" aria-hidden="true">{c.sort === "asc" ? "↑" : "↓"}</span>}{c.warn && <span className="g-warn" aria-hidden="true">!</span>}
                </button>
                {onResize && <span className="g-rz" onMouseDown={(e) => startResize(e, c)} aria-hidden="true" />}
              </th>
            ))}
            <th className="g-fill" />
          </tr>
        </thead>
        <tbody>
          {first > 0 && <tr style={{ height: first * ROW_H }} aria-hidden="true"><td colSpan={cols.length + 2} /></tr>}
          {rows.slice(first, last).map((row, k) => rowEl(row, first + k))}
          {last < rows.length && <tr style={{ height: (rows.length - last) * ROW_H }} aria-hidden="true"><td colSpan={cols.length + 2} /></tr>}
        </tbody>
        {footer && <tfoot>{rowEl(footer, -1, true)}</tfoot>}
      </table>
      {!rows.length && empty ? <div className="g-empty">{empty}</div> : null}
    </div>
  );
}
